import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getEligibleContacts, getTaggedContacts, tagContact, untagContact } from 'wisestep-api';
import { ScrollableTabs } from 'wisestep-base';

import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { ContactFormValues } from '../helper/crmJobHelper';
import { useCrmJobSettings } from '../hooks/useCrmJobSettings';

import CrmContactCheckboxList from './CrmContactCheckboxList';

interface CrmContactsDrawerProps {
  jobId: string;
  defaultTab?: number;
}

const CrmContactsDrawer = ({ jobId, defaultTab = 0 }: CrmContactsDrawerProps) => {
  const [tab, setTab] = useState(defaultTab);
  const [eligibleContacts, setEligibleContacts] = useState([]);
  const [taggedContacts, setTaggedContacts] = useState([]);
  const workspaceContext = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const reviewContactCount = useCrmJobSettings(workspaceContext?.id ?? '');

  const methods = useForm<ContactFormValues>();
  const { watch, setValue, reset } = methods;

  const loadContacts = async () => {
    try {
      const [eligibleRes, taggedRes] = await Promise.all([
        getEligibleContacts(jobId).catch((err) => {
          console.error('Error loading eligible contacts:', err);
          return { data: [] };
        }),
        getTaggedContacts(jobId).catch((err) => {
          console.error('Error loading tagged contacts:', err);
          return { data: [] };
        }),
      ]);

      setEligibleContacts(eligibleRes.data);
      setTaggedContacts(taggedRes.data);
    } catch (error) {
      console.error('Unexpected error loading contacts:', error);
      setEligibleContacts([]);
      setTaggedContacts([]);
    }
  };

  useEffect(() => {
    jobId && loadContacts();
  }, [jobId, workspaceContext?.id]);

  const tagContacts = async () => {
    try {
      const selected = watch('contactList') ?? [];
      await Promise.all(selected.map((id) => tagContact(jobId, id)));
      reset({ contactList: [] });
      await loadContacts();
      setTab(1);
    } catch (error) {
      console.error('Error tagging contacts:', error);
    }
  };

  const untagContacts = async () => {
    const selected = watch('contactList') || [];
    await Promise.all(selected.map((id) => untagContact(jobId, id)));
    reset({ contactList: [] });
    loadContacts();
  };

  const handleSelectAll = (checked, list) => {
    if (checked) {
      const allIds = list.map((c) => c.id);
      setValue('contactList', allIds);
    } else {
      setValue('contactList', []);
    }
  };

  return (
    <Box>
      <ScrollableTabs
        scrollList={[
          { tabName: 'Eligible Contacts' },
          { tabName: 'Mailing List', tabCount: taggedContacts.length },
        ]}
        getTabIndex={(newTabIndex) => {
          setTab(newTabIndex);
          reset({ contactList: [] });
        }}
        defaultValue={tab}
      />
      <Box m={2}>
        {tab === 0 && (
          <CrmContactCheckboxList
            contacts={eligibleContacts}
            isTagged={false}
            methods={methods}
            onAssign={tagContacts}
            onSelectAll={handleSelectAll}
            reviewContactCount={reviewContactCount}
            setNotification={setNotification}
            assignedContactsCount={taggedContacts.length}
          />
        )}
        {tab === 1 && (
          <CrmContactCheckboxList
            contacts={taggedContacts}
            isTagged={true}
            methods={methods}
            onAssign={untagContacts}
            onSelectAll={handleSelectAll}
          />
        )}
      </Box>
    </Box>
  );
};

export default CrmContactsDrawer;
